import {
  Component,
  ChangeDetectionStrategy,
  computed,
  signal,
  inject,
  effect,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { CdkDrag, CdkDragEnd } from '@angular/cdk/drag-drop';

// Components
import { DialogComponent } from '../dialog/confirm.dialog';

// Services
import { GlobalCallContextService } from '../../services/global-call-context.service';
import { GlobalCallPanelService } from '../../services/global-call-panel.service';
import { CallFacadeService } from '../../services/call/call-facade.service';
import { TelnyxWebRTCService } from '../../services/telnyx-webrtc.service';

// Types
import { CallState } from '../../services/call/call-types';

/**
 * GlobalCallPanelComponent
 *
 * A floating, draggable call panel that persists across navigation.
 * Provides a global call interface independent of route components.
 *
 * Features:
 * - Fixed position with drag-to-move capability
 * - Minimize/expand states
 * - Call controls (mute, hold, end)
 * - Lead context display
 * - Duration timer
 * - Connection status
 *
 * Usage:
 * Add to app.component.html:
 * <app-global-call-panel />
 */
@Component({
  selector: 'app-global-call-panel',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    CdkDrag
  ],
  templateUrl: './global-call-panel.component.html',
  styleUrl: './global-call-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalCallPanelComponent implements OnInit, OnDestroy {
  // ============================================================================
  // Service Injection
  // ============================================================================

  private readonly contextService = inject(GlobalCallContextService);
  private readonly panelService = inject(GlobalCallPanelService);
  private readonly callFacade = inject(CallFacadeService);
  private readonly telnyxService = inject(TelnyxWebRTCService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  // ============================================================================
  // Panel State Signals (from services)
  // ============================================================================

  /** Whether panel is visible */
  readonly isVisible = this.panelService.isVisible;

  /** Whether panel is minimized */
  readonly isMinimized = this.panelService.isMinimized;

  /** Panel position */
  readonly position = this.panelService.position;

  // ============================================================================
  // Call Context Signals (from services)
  // ============================================================================

  /** Lead display name */
  readonly leadName = this.contextService.leadDisplayName;

  /** Phone number */
  readonly phoneNumber = this.contextService.phoneNumber;

  /** Lead initials for avatar */
  readonly leadInitials = this.contextService.leadInitials;

  /** Call direction */
  readonly direction = this.contextService.direction;

  /** Lead ID for navigation */
  readonly leadId = this.contextService.leadId;

  // ============================================================================
  // Call State Signals (from services)
  // ============================================================================

  /** Current call state */
  readonly currentState = this.callFacade.currentState;

  /** Call duration in seconds */
  readonly callDuration = this.callFacade.callDuration;

  /** Is muted */
  readonly isMuted = computed(() => this.telnyxService.isMuted());

  /** Is on hold */
  readonly isOnHold = computed(() => this.currentState() === 'held');

  /** WebRTC connection status */
  readonly isConnected = this.callFacade.isConnected;

  // ============================================================================
  // Local UI State
  // ============================================================================

  /** Internal drag position tracking (for CDK Drag) */
  private dragPosition = signal({ x: 0, y: 0 });

  /** Position sync effect - updates drag position when service position changes */
  private positionSyncEffect = effect(() => {
    const pos = this.effectivePosition();
    this.dragPosition.set({ x: pos.left, y: pos.top });
  }, { allowSignalWrites: true });

  // ============================================================================
  // Computed Signals
  // ============================================================================

  /**
   * Formatted call duration (MM:SS)
   */
  readonly formattedDuration = computed(() => {
    const duration = this.callDuration();
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });

  /**
   * Call state label for display
   */
  readonly callStateLabel = computed(() => {
    const state = this.currentState();
    const stateLabels: Record<CallState, string> = {
      'idle': 'Idle',
      'dialing': 'Dialing',
      'ringing': 'Ringing',
      'connecting': 'Connecting',
      'connected': 'Connected',
      'holding': 'Holding',
      'held': 'On Hold',
      'ending': 'Ending',
      'ended': 'Ended',
      'post-call': 'Post-Call',
      'failed': 'Failed'
    };
    return stateLabels[state] || state;
  });

  /**
   * Call state icon for display
   */
  readonly callStateIcon = computed(() => {
    const state = this.currentState();
    const stateIcons: Record<CallState, string> = {
      'idle': 'phone',
      'dialing': 'phone_in_talk',
      'ringing': 'ring_volume',
      'connecting': 'sync',
      'connected': 'call',
      'holding': 'pause',
      'held': 'pause_circle',
      'ending': 'call_end',
      'ended': 'call_end',
      'post-call': 'assignment_turned_in',
      'failed': 'error'
    };
    return stateIcons[state] || 'phone';
  });

  /**
   * Direction badge text
   */
  readonly directionText = computed(() => {
    const dir = this.direction();
    return dir === 'inbound' ? 'Incoming' : dir === 'outbound' ? 'Outgoing' : '';
  });

  /**
   * Direction badge icon
   */
  readonly directionIcon = computed(() => {
    const dir = this.direction();
    return dir === 'inbound' ? 'call_received' : 'call_made';
  });

  /**
   * Whether call is in an active state (for pulsing animation)
   */
  readonly isCallActive = computed(() => {
    const state = this.currentState();
    return ['dialing', 'ringing', 'connecting', 'connected', 'held'].includes(state);
  });

  /**
   * Can toggle mute (only when connected or held)
   */
  readonly canToggleMute = computed(() => {
    const state = this.currentState();
    return state === 'connected' || state === 'held';
  });

  /**
   * Can toggle hold (only when connected or held)
   */
  readonly canToggleHold = computed(() => {
    const state = this.currentState();
    return state === 'connected' || state === 'held';
  });

  /**
   * Effective position for rendering (handles default bottom-right)
   */
  readonly effectivePosition = computed(() => {
    return this.panelService.getEffectivePosition();
  });

  // ============================================================================
  // Lifecycle
  // ============================================================================

  ngOnInit(): void {
    // Position sync handled by field initializer effect
  }

  ngOnDestroy(): void {
    // Cleanup handled by services
  }

  // ============================================================================
  // Panel Actions
  // ============================================================================

  /**
   * Toggle minimized state
   */
  toggleMinimize(): void {
    this.panelService.toggleMinimize();
  }

  /**
   * Close panel with confirmation - ends the call if confirmed
   */
  closePanel(): void {
    // Suppress auto-show while dialog is open
    this.panelService.setDialogOpen(true);
    // Hide panel so dialog is visible
    this.panelService.hidePanel();

    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        header: 'End Call?',
        content: 'Are you sure you want to end this call?',
        type: 'warning',
        buttonName: 'End Call'
      },
      width: '400px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(async (confirmed) => {
      // Re-enable auto-show
      this.panelService.setDialogOpen(false);

      if (confirmed) {
        // Use cancelCall for dialing/ringing states, endCall otherwise
        const state = this.currentState();
        if (['dialing', 'ringing', 'connecting'].includes(state)) {
          await this.callFacade.cancelCall();
        } else {
          await this.callFacade.endCall();
        }
        // Reset panel state - don't use closeByUser() as it blocks future auto-show
        this.panelService.reset();
      } else {
        // User cancelled - show panel again
        this.panelService.showPanel();
      }
    });
  }

  /**
   * Expand panel (if minimized)
   */
  expandPanel(): void {
    if (this.isMinimized()) {
      this.panelService.expand();
    }
  }

  // ============================================================================
  // Call Control Actions
  // ============================================================================

  /**
   * End the current call
   */
  async endCall(): Promise<void> {
    await this.callFacade.endCall();
  }

  /**
   * Toggle mute state
   */
  toggleMute(): void {
    if (this.canToggleMute()) {
      this.telnyxService.toggleMute();
    }
  }

  /**
   * Toggle hold state
   */
  toggleHold(): void {
    if (this.canToggleHold()) {
      this.callFacade.toggleHold();
    }
  }

  /**
   * Navigate to lead view page
   */
  goToLead(): void {
    const id = this.leadId();
    if (id) {
      this.router.navigate(['/leads/leads-managment/manage', id]);
    }
  }

  // ============================================================================
  // Drag Handling
  // ============================================================================

  /**
   * Handle drag ended event
   * Save new position to service
   */
  onDragEnded(event: CdkDragEnd): void {
    const element = event.source.element.nativeElement;
    const rect = element.getBoundingClientRect();

    // Get the new position (top-left corner)
    const newPosition = {
      x: rect.left,
      y: rect.top
    };

    // Update service with new position
    this.panelService.updatePosition(newPosition);

    // Reset CDK drag transform to prevent offset accumulation
    event.source.reset();
  }

}
